import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { FaXmark } from "react-icons/fa6";
import Button from "@components/forms/Button/Button";
import TextField from "@components/forms/TextField/TextField";
import { TwitchStreamer } from "@ltypes/scene";
import './StreamerList.scss';

type StreamerListProps = {
    label: string,
    streamers: Array<TwitchStreamer>,
    onChange: (streamers: Array<TwitchStreamer>) => void,
};

const parseTwitchInput = (input: string): string => {
    try {
        const url = new URL(input.includes('://') ? input : `https://${input}`);
        if (url.hostname.endsWith('twitch.tv')) {
            const segment = url.pathname.split('/').filter(Boolean)[0];
            return segment ?? input;
        }
    } catch {}
    return input;
};

function StreamerList(props: StreamerListProps) {
    const [inputValue, setInputValue] = useState<string>('');
    const { t } = useTranslation();

    const handleAdd = () => {
        const trimmed = inputValue.trim();
        if (!trimmed) return;
        const login = parseTwitchInput(trimmed);
        if (!login || props.streamers.find(s => s.streamer_login === login)) return;
        props.onChange([...props.streamers, {
            streamer_id: null,
            streamer_login: login,
            streamer_name: login,
            profile_image_url: null,
            is_live: null,
        }]);
        setInputValue('');
    };

    const handleDelete = (login: string) => {
        props.onChange(props.streamers.filter(s => s.streamer_login !== login));
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAdd();
        }
    };

    return (
        <div className="streamer-list">
            <span className="streamer-list-label">{props.label}</span>
            <ul className="input-list">
                {props.streamers.map((streamer, index) => (
                    <li key={index}>
                        <div className="avatar" style={streamer.profile_image_url ? {
                            backgroundImage: `url("${streamer.profile_image_url}")`,
                        } : undefined}/>
                        <span className="name">{streamer.streamer_name ?? streamer.streamer_login}</span>
                        {streamer.is_live && <span className="live-badge">LIVE</span>}
                        <div className="actions">
                            <span onClick={() => handleDelete(streamer.streamer_login)}><FaXmark size="1.1rem"/></span>
                        </div>
                    </li>
                ))}
            </ul>
            <div className="row g-1 align-items-center" onKeyDown={handleKeyDown}>
                <div className="col">
                    <TextField placeholder="xQc or https://www.twitch.tv/xQc" value={inputValue} onChange={setInputValue}/>
                </div>
                <div className="col-auto">
                    <Button size="sm" filled onClick={handleAdd}>{t('text.add')}</Button>
                </div>
            </div>
        </div>
    );
}

export default StreamerList;
